import { AddressValidationResult } from 'src/app/services/address-validation.service';

export interface Coordinates {
  latitude: number;
  longitude: number;
}

interface ResolveLocationCoordinatesOptions {
  locationType: string;
  directCoordinates?: Coordinates;
  unchangedCoordinates?: Coordinates;
  homeCoordinates?: Coordinates;
  hasMatchingHomeCoordinates: boolean;
  addressToValidate: string;
  sourceLabel?: string;
  validateAddress: (address: string) => Promise<AddressValidationResult>;
}

export function isValidCoordinates(coordinates?: {
  latitude: number | undefined;
  longitude: number | undefined;
}): coordinates is Coordinates {
  if (!coordinates) {
    return false;
  }

  return (
    Number.isFinite(coordinates.latitude) &&
    Number.isFinite(coordinates.longitude) &&
    coordinates.latitude !== 0 &&
    coordinates.longitude !== 0 &&
    coordinates.latitude >= -90 &&
    coordinates.latitude <= 90 &&
    coordinates.longitude >= -180 &&
    coordinates.longitude <= 180
  );
}

export function hasMatchingAddressCoordinates(
  address: { address?: string; coordinates?: Coordinates; coordinatesFor?: string } | undefined,
  normalizeAddressForComparison: (address: string) => string,
): address is { address: string; coordinates: Coordinates; coordinatesFor?: string } {
  if (!address?.coordinates || !isValidCoordinates(address.coordinates)) {
    return false;
  }

  if (!address.coordinatesFor) {
    return true;
  }

  return address.coordinatesFor === normalizeAddressForComparison(String(address.address ?? ''));
}

export function areRideLocationsEqual(
  type: string,
  currentLocation: unknown,
  previousLocation: unknown,
  normalizeAddressForComparison: (address: string) => string,
): boolean {
  if (type === 'addressFromDb') {
    const currentId =
      typeof currentLocation === 'object' && currentLocation
        ? (currentLocation as { id?: string }).id
        : currentLocation;
    const previousId =
      typeof previousLocation === 'object' && previousLocation
        ? (previousLocation as { id?: string }).id
        : previousLocation;
    return currentId === previousId;
  }

  const currentAddress =
    typeof currentLocation === 'object' && currentLocation
      ? (currentLocation as { address?: string }).address
      : currentLocation;
  const previousAddress =
    typeof previousLocation === 'object' && previousLocation
      ? (previousLocation as { address?: string }).address
      : previousLocation;

  return (
    normalizeAddressForComparison(String(currentAddress ?? '')) ===
    normalizeAddressForComparison(String(previousAddress ?? ''))
  );
}

export function getUnchangedRideCoordinates(
  currentType: string,
  currentLocation: unknown,
  previousType: string | undefined,
  previousLocation: unknown,
  longitude: number | undefined,
  latitude: number | undefined,
  normalizeAddressForComparison: (address: string) => string,
): Coordinates | undefined {
  if (
    !isValidCoordinates({ latitude, longitude }) ||
    currentType !== previousType ||
    !areRideLocationsEqual(
      currentType,
      currentLocation,
      previousLocation,
      normalizeAddressForComparison,
    )
  ) {
    return undefined;
  }

  return { latitude, longitude };
}

export async function resolveLocationCoordinates(
  options: ResolveLocationCoordinatesOptions,
): Promise<AddressValidationResult> {
  const {
    locationType,
    directCoordinates,
    unchangedCoordinates,
    homeCoordinates,
    hasMatchingHomeCoordinates,
    addressToValidate,
    sourceLabel,
    validateAddress,
  } = options;
  const label = sourceLabel ?? 'unknown';

  if (isValidCoordinates(directCoordinates)) {
    console.info('[CoordinateResolver] Reused coordinates from address document', {
      sourceLabel: label,
      locationType,
      address: addressToValidate,
    });
    return {
      isValid: true,
      cleanedAddress: '',
      originalAddress: addressToValidate,
      coordinates: directCoordinates,
    };
  }

  if (isValidCoordinates(unchangedCoordinates)) {
    console.info('[CoordinateResolver] Reused unchanged ride coordinates', {
      sourceLabel: label,
      locationType,
      address: addressToValidate,
    });
    return {
      isValid: true,
      cleanedAddress: '',
      originalAddress: addressToValidate,
      coordinates: unchangedCoordinates,
    };
  }

  if (
    locationType === 'home' &&
    hasMatchingHomeCoordinates &&
    isValidCoordinates(homeCoordinates)
  ) {
    console.info('[CoordinateResolver] Reused home coordinates from user document', {
      sourceLabel: label,
      locationType,
      address: addressToValidate,
    });
    return {
      isValid: true,
      cleanedAddress: '',
      originalAddress: addressToValidate,
      coordinates: homeCoordinates,
    };
  }
  console.info('[CoordinateResolver] Fetching coordinates from ORS API', {
    sourceLabel: label,
    locationType,
    address: addressToValidate,
  });
  return validateAddress(addressToValidate);
}
